import React from "react";
import {
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Box,
  Button,
} from "@mui/material";

import { styled } from "@mui/material/styles";

import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import NotesIcon from "@mui/icons-material/Notes";

// -------- Styled components matching Delivery -------- //
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
  borderRadius: "20px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
  padding: "48px",
  maxWidth: "1200px",
  margin: "0 auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid rgba(0, 0, 0, 0.06)",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
      backgroundColor: "#ffffff",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 16px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: "500",
    "&::placeholder": {
      opacity: "0.6",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "14px",
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    paddingLeft: "6px",
    paddingRight: "6px",
    marginLeft: "-3px",
    "&.Mui-focused": {
      color: "primary.main",
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    marginLeft: "0",
    color: "#718096",
    marginTop: "8px",
  },
}));

const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid rgba(0, 0, 0, 0.05)",
  "&:first-of-type": {
    marginTop: 0,
    paddingTop: 0,
    borderTop: "none",
  },
});

const IconWrapper = styled(Box)({
  width: "28px",
  height: "28px",
  borderRadius: "8px",
  backgroundColor: "rgba(25,118,210,0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": { width: "16px", height: "16px" },
});

const SectionLabel = styled(Typography)({
  textTransform: "uppercase",
  letterSpacing: "1px",
  fontWeight: 700,
  fontSize: "14px",
  color: "primary.main",
});

const HeadingBox = styled(Box)({
  marginBottom: "40px",
  paddingBottom: "32px",
  borderBottom: "2px solid rgba(0, 0, 0, 0.05)",
});

const FormGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
}));

const FullWidthGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
}));

const AssistanceBox = styled(Box)({
  padding: "20px",
  borderRadius: "12px",
  background:
    "linear-gradient(135deg, rgba(25,118,210,0.04), rgba(25,118,210,0.02))",
  border: "1px solid rgba(25,118,210,0.1)",
  marginTop: "24px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
});

export const defaultAddress = () => ({
  firstName: "",
  lastName: "",

  fullName: "",
  phone: "",
  email: "",
  country: "",
  state: "",
  city: "",
  postcode: "",
  address1: "",
  address2: "",
  companyName: "",

  vat: "",
  landmark: "",
  locationType: "",
  assistanceAvailable: false,

  pickupType: "own",
  forkliftAvailable: false,
  specialNotes: "",
  timeSlot: "",
  images: [],
  documents: [],

  equipment: "",
  notes: "",

  vehicleReg: "",
  truckType: "",
  driverName: "",
  driverPhone: "",

  pickupDocketDate: "",
  pickupDocketTime: "",
  pickupConfirmationNumber: "",
  pickupCarrier: "",
  pickupConNote: "",

  deliveryStartDate: "",
  deliveryEndDate: "",
  deliveryTimeSlot: "",
});
// -------------------------------------------------------------------- //

export default function BillingAddressForm({
  billing= defaultAddress(),
  onChange,
  onToggle,
  errors,
}) {
  const handleFieldChange = (field) => (e) => onChange(field)(e.target.value);

  const handleToggleChange = (field) => (e) =>
    onToggle && onToggle(field)(e.target.checked);
  return (
    <StyledPaper>
      {/* HEADER */}
      <HeadingBox>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "800",
            fontSize: "30px",
            background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "12px",
          }}
        >
          Billing Address
        </Typography>

        <Typography
          sx={{ color: "#718096", fontSize: "15px", fontWeight: 500 }}
        >
          Required for every order
        </Typography>
      </HeadingBox>

      {/* CONTACT */}
      <SectionTitle>
        <IconWrapper>
          <PersonIcon />
        </IconWrapper>
        <SectionLabel>Contact Information</SectionLabel>
      </SectionTitle>

      <FormGrid>
        <StyledTextField
          fullWidth
          label="First Name *"
          value={billing.firstName}
          onChange={handleFieldChange("firstName")}
          error={!!errors?.firstName}
          helperText={errors?.firstName}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Last Name *"
          value={billing.lastName}
          onChange={handleFieldChange("lastName")}
          error={!!errors?.lastName}
          helperText={errors?.lastName}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Phone *"
          value={billing.phone}
          onChange={handleFieldChange("phone")}
          error={!!errors?.phone}
          helperText={errors?.phone}
        />

        <StyledTextField
          fullWidth
          label="Email *"
          value={billing.email}
          onChange={handleFieldChange("email")}
          error={!!errors?.email}
          helperText={errors?.email}
        />
        <StyledTextField
          fullWidth
          label="Company Name *"
          value={billing.companyName}
          onChange={handleFieldChange("companyName")}
          error={!!errors?.companyName}
          helperText={errors?.companyName}
        />
        <StyledTextField
          fullWidth
          label="ABN/ACN/VAT/Tax ID *"
          value={billing.vat}
          onChange={handleFieldChange("vat")}
          error={!!errors?.vat}
          helperText={errors?.vat}
          variant="outlined"
        />
      </FormGrid>

      {/* LOCATION */}
      <SectionTitle>
        <IconWrapper>
          <LocationOnIcon />
        </IconWrapper>
        <SectionLabel>Location Details</SectionLabel>
      </SectionTitle>

      <FormGrid>
        <StyledTextField
          fullWidth
          label="Country *"
          value={billing.country}
          onChange={handleFieldChange("country")}
          error={!!errors?.country}
          helperText={errors?.country}
        />
        <StyledTextField
          fullWidth
          label="State *"
          value={billing.state}
          onChange={handleFieldChange("state")}
          error={!!errors?.state}
          helperText={errors?.state}
        />
        <StyledTextField
          fullWidth
          label="City *"
          value={billing.city}
          onChange={handleFieldChange("city")}
          error={!!errors?.city}
          helperText={errors?.city}
        />
        <StyledTextField
          fullWidth
          label="Postcode *"
          value={billing.postcode}
          onChange={handleFieldChange("postcode")}
          error={!!errors?.postcode}
          helperText={errors?.postcode}
        />
      </FormGrid>

      {/* ADDRESS */}
      <SectionTitle>
        <IconWrapper>
          <HomeIcon />
        </IconWrapper>
        <SectionLabel>Billing Address</SectionLabel>
      </SectionTitle>

      <FormGrid>
        <StyledTextField
          fullWidth
          label="Address Line 1 *"
          value={billing.addressLine1}
          onChange={handleFieldChange("addressLine1")}
          error={!!errors?.addressLine1}
          helperText={errors?.addressLine1}
        />
        <StyledTextField
          fullWidth
          label="Address Line 2 (optional)"
          value={billing.addressLine2}
          onChange={handleFieldChange("addressLine2")}
          error={!!errors?.addressLine2}
          helperText={errors?.addressLine2}
        />
        <StyledTextField
          fullWidth
          label="Landmark (optional)"
          value={billing.landmark}
          onChange={handleFieldChange("landmark")}
          error={!!errors?.landmark}
          helperText={errors?.landmark}
        />
      </FormGrid>

      {/* NOTES */}
      {/* <SectionTitle>
        <IconWrapper>
          <NotesIcon />
        </IconWrapper>
        <SectionLabel>Billing Notes</SectionLabel>
      </SectionTitle>

      <StyledTextField
        fullWidth
        label="Billing Notes (Optional)"
        multiline
        minRows={5}
        value={billing.notes}
        onChange={handleFieldChange("notes")}
        helperText="Note: Include any special instructions if needed"
      /> */}
    </StyledPaper>
  );
}
